import { appConfig } from '../app-config';
import { authStore } from '../auth/auth-store';
import { debug } from '../utils/logger';
import type { WSMessage } from './messages';

/**
 * WebSocket client.
 *
 * Rewritten rather than ported. The original had four separate timers whose
 * callbacks re-entered each other — `handleBrokenConnection` cleared the
 * reconnect timer and then set a new one, while `connect` early-returned if
 * `this.ws` was non-null, so a socket that failed to open could leave the app
 * permanently disconnected with a timer still firing. It also had an unused
 * ping timer sending nothing, and imported `T` from ramda without using it.
 *
 * The rules here:
 *  - one timer, always cleared before being set
 *  - backoff, so a server restart does not get hammered once per second
 *  - the token is read at connect time, never captured, so signing in or out
 *    changes what the next connection authenticates as
 */

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_MAX_MS = 30_000;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Handler = (payload?: any) => void;

/**
 * Minimal event emitter.
 *
 * Node's `events` is not a browser module — webpack silently polyfilled it, and
 * Vite (correctly) does not. Rather than pulling in a shim for four methods,
 * this is the four methods. It is also typed, where the polyfill's `on(cmd,
 * cb)` was `any`.
 */
class Emitter {
  private handlers = new Map<string, Set<Handler>>();

  on(event: string, handler: Handler): void {
    let set = this.handlers.get(event);
    if (!set) this.handlers.set(event, (set = new Set()));
    set.add(handler);
  }

  off(event: string, handler: Handler): void {
    this.handlers.get(event)?.delete(handler);
  }

  removeAllListeners(event: string): void {
    this.handlers.delete(event);
  }

  protected emit(event: string, payload?: unknown): void {
    // Copy before iterating: a handler may unsubscribe itself.
    for (const handler of [...(this.handlers.get(event) ?? [])]) {
      try {
        handler(payload);
      } catch (err) {
        // One bad subscriber must not stop the others from being told.
        debug('mobile-socket: listener threw', { err });
      }
    }
  }
}

export class SocketClient extends Emitter {
  private socket: WebSocket | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private attempts = 0;
  private wanted = false;

  private static instance: SocketClient | null = null;

  static getInstance(): SocketClient {
    if (!this.instance) this.instance = new SocketClient();
    return this.instance;
  }

  isOpen(): boolean {
    return this.socket?.readyState === WebSocket.OPEN;
  }

  /** Token in the query string: a browser cannot set headers on an upgrade. */
  private url(): string | null {
    const session = authStore.get();
    if (!session) return null;
    return `${appConfig.socketUrl}?token=${encodeURIComponent(session.token)}`;
  }

  connect(): void {
    this.wanted = true;
    if (this.socket || this.reconnectTimer) return;

    const url = this.url();
    if (!url) {
      // Signed out: nothing to connect as. Reconnecting is driven by sign-in,
      // not by a timer that would spin forever on the sign-in screen.
      debug('mobile-socket: no session, not connecting');
      return;
    }

    const socket = new WebSocket(url);
    this.socket = socket;

    socket.addEventListener('open', () => {
      this.attempts = 0;
      debug('mobile-socket: open');
      this.emit('open');
    });

    socket.addEventListener('message', (event: MessageEvent<string>) => {
      let message: { cmd?: string };
      try {
        message = JSON.parse(event.data) as { cmd?: string };
      } catch {
        debug('mobile-socket: unparseable frame');
        return;
      }
      if (!message.cmd) return;

      /**
       * The server closes the socket after this, so treat it as the session
       * being over rather than as a transport failure — otherwise the client
       * reconnects forever with a token the server keeps rejecting.
       */
      if (message.cmd === 'qmob/unauthorized') {
        this.wanted = false;
        authStore.clear();
        this.emit('unauthorized');
        return;
      }
      this.emit(message.cmd, message);
    });

    socket.addEventListener('error', () => {
      debug('mobile-socket: error');
    });

    socket.addEventListener('close', () => {
      debug('mobile-socket: closed');
      this.socket = null;
      this.emit('close');
      this.scheduleReconnect();
    });
  }

  private scheduleReconnect(): void {
    if (!this.wanted || this.reconnectTimer) return;

    // Exponential backoff capped at 30s. The old client retried every second
    // forever, which turned a server restart into a burst of connections at
    // the exact moment it was least able to take them.
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** this.attempts, RECONNECT_MAX_MS);
    this.attempts += 1;

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  reconnect(): void {
    this.disconnect();
    this.wanted = true;
    this.attempts = 0;
    this.connect();
  }

  disconnect(): void {
    this.wanted = false;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    const socket = this.socket;
    this.socket = null;
    socket?.close();
  }

  sendMessage(message: WSMessage): void {
    if (!this.isOpen()) {
      debug('mobile-socket: dropped message, socket not open', message.cmd);
      return;
    }
    this.socket?.send(JSON.stringify(message));
  }
}
